import AxiosHealthWellness from '.';
import axios from 'axios';
import config from '../../config';
import { HealthSpanProfile } from '../../model/HealthSpanProfile.model';

const SKIP_LIFF = process.env.REACT_APP_SKIP_LIFF === 'true';

// Use dev axios instance when LIFF is skipped (localhost dev)
const devAxios = axios.create({ timeout: 30000 });
devAxios.interceptors.request.use((cfg) => {
  const token = localStorage.getItem('devToken');
  if (token) {
    cfg.headers.authorization = `Bearer ${token}`;
  }
  const customer = JSON.parse(sessionStorage.getItem('customer'));
  if (customer) {
    cfg.headers.account = customer.amwayNumber || '';
    cfg.headers.customer = customer.partyId || '';
  }
  return cfg;
});

const http = SKIP_LIFF ? devAxios : AxiosHealthWellness;

/**
 * @typedef {Object} HealthProfileScoreByCategory
 * @property {number} GutHealth
 * @property {number} Lifestyle
 * @property {number} MentalEmotionalHealth
 * @property {number} MetabolicHealth
 * @property {number} PhysicalFitness
 * @property {number} SkinHealth
 */

/**
 * @typedef {Object} LifestyleSubCategories
 * @property {number} HealthBehavior
 * @property {number} Hydration
 * @property {number} NutritionQuality
 * @property {number} Sleep
 */

/**
 * @typedef {Object} MetabolicHealthSubCategories
 * @property {number} BMI
 * @property {number} BodyFatPercentage
 * @property {number} WaistCircumference
 */

/**
 * @typedef {Object} PhysicalFitnessSubCategories
 * @property {number} DailySteps
 * @property {number} ExerciseBehavior
 * @property {number} ExerciseConfidence
 * @property {number} MuscleMass
 */

/**
 * @typedef {Object} MentalEmotionalHealthSubCategories
 * @property {number} StressData
 */

/**
 * @typedef {Object} GutHealthSubCategories
 * @property {number} GutBalanceData
 */

/**
 * @typedef {Object} SkinHealthSubCategories
 * @property {number} DarkSpots
 * @property {number} Other
 * @property {number} SkinLaxity
 * @property {number} Wrinkles
 */

/**
 * @typedef {Object} HealthProfileScoreDetail
 * @property {string} _id
 * @property {string} profile_id
 * @property {'Lifestyle'|'MetabolicHealth'|'PhysicalFitness'|'MentalEmotionalHealth'|'GutHealth'|'SkinHealth'} category
 * @property {number} score
 * @property {LifestyleSubCategories|MetabolicHealthSubCategories|PhysicalFitnessSubCategories|MentalEmotionalHealthSubCategories|GutHealthSubCategories|SkinHealthSubCategories} score_by_sub_category
 * @property {string} created_at
 * @property {string} updated_at
 * @property {string|null} deleted_at
 */

/**
 * @typedef {Object} HealthProfile
 * @property {string} profile_id
 * @property {number} total_score
 * @property {HealthProfileScoreByCategory} score_by_category
 * @property {string[]} score_detail_ids
 * @property {string} created_at
 * @property {string} updated_at
 * @property {string|null} deleted_at
 * @property {HealthProfileScoreDetail[]} score_details
 */

/**
 * Raw API response for GET /health-span-profile/health-profile
 * See sample: ./healthSpan.health-profile.sample.json
 *
 * @typedef {Object} HealthSpanProfileApiResponse
 * @property {string} generated_at
 * @property {HealthProfile} health_profile
 */

/**
 * Profile status constants
 */
export const PROFILE_STATUS = {
  NOT_FOUND: 'not_found',
  PROCESSING: 'processing',
  READY: 'ready',
};

/**
 * Fetch health span profile data
 * GET /health-span-profile/health-profile
 * @see ./healthSpan.health-profile.sample.json for response shape
 * @returns {Promise<{ status: string, profile: HealthSpanProfile|null }>}
 */
export const fetchHealthSpanProfile = async () => {
  try {
    const response = await http.get(
      `${config.API_URL}/health-span-profile/health-profile`
    );
    const data = response?.data;

    if (data?.status === PROFILE_STATUS.NOT_FOUND) {
      return { status: PROFILE_STATUS.NOT_FOUND, profile: null };
    }

    if (data?.status === PROFILE_STATUS.PROCESSING) {
      return { status: PROFILE_STATUS.PROCESSING, profile: null };
    }

    return { status: PROFILE_STATUS.READY, profile: new HealthSpanProfile(data) };
  } catch (error) {
    console.error('Error fetching health span profile:', error);
    throw error;
  }
};

/**
 * Delete health span profile data
 * DELETE /health-span-profile/health-profile
 * @returns {Promise<void>}
 */
export const deleteHealthSpanProfile = async () => {
  try {
    const response = await http.delete(
      `${config.API_URL}/health-span-profile/health-profile`
    );
    return response?.data;
  } catch (error) {
    console.error('Error deleting health span profile:', error);
    throw error;
  }
};

/**
 * Fetch score category metadata (levels, tip, labels)
 * GET /health-span-profile/score-category-metadata/{category}
 * @param {string} category - e.g. health_span_lifestyle
 * @returns {Promise<{ category: string, metadata: Object }>}
 */
export const fetchScoreCategoryMetadata = async (category) => {
  try {
    const response = await http.get(
      `${config.API_URL}/health-span-profile/score-category-metadata/${category}`
    );
    return response?.data;
  } catch (error) {
    console.error('Error fetching score category metadata:', error);
    throw error;
  }
};
